import {ChangeDetectionStrategy, Component} from '@angular/core';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ActivatedRoute, Router} from "@angular/router";
import {SnackbarService} from "../../_services/snackbar.service";
import {LightboxComponent} from "../../shared/lightbox/lightbox.component";
import {ConfirmDialogComponent} from "../../shared/confirm-dialog/confirm-dialog.component";
import {ConfirmationDialogData} from "../../_types/shared";
import {ContentService} from "../../_services/content.service";
import {FormBuilder, FormGroup} from "@angular/forms";
import {AuthService} from "../../_services/auth.service";
import {MoveMediaComponent} from "../../admin/flagged-media/move-media/move-media.component";
import {User} from "../../_types/user";
import {MediaItem, MemoryWithCoverImage} from "../../_types/memorybox";
import {HiddenMediaNotes} from "../../_types/admin";

@Component({
    selector: 'app-hidden-media-details',
    templateUrl: './hidden-media-details.component.html',
    styleUrl: './hidden-media-details.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HiddenMediaDetailsComponent {
    media: MediaItem | undefined;
    id: number | undefined;
    timelineId: number | undefined;
    hiddenForm: FormGroup;
    notes: HiddenMediaNotes[] = [];
    user!: User;
    today: Date = new Date();
    messageForm: FormGroup;
    loading: boolean = false;
    reLoading: boolean = false;
    album: MemoryWithCoverImage | undefined;

    constructor(public dialog: MatDialog,
                private route: ActivatedRoute,
                private router: Router,
                private authService: AuthService,
                private snackbarService: SnackbarService,
                private contentService: ContentService,
                private readonly formBuilder: FormBuilder) {
        this.authService.getUser().subscribe({
            next: r => {
                this.user = r;
            }
        })

        this.messageForm = this.formBuilder.group({
            message: [''],
        });

        this.route.params
            .subscribe(params => {
                this.id = JSON.parse(params['id']);

                if (!this.id) return;
                this.contentService.getMedia(this.id).subscribe({
                    next: r => {
                        this.media = r;
                        this.hiddenForm.patchValue({
                            hidden_status: this.media.hidden_status,
                        })
                        this.contentService.getMemory(r.memory).subscribe({
                            next: res => {
                                this.album = res;
                                this.timelineId = res.timeline.id;
                            }, error: error => {
                                this.snackbarService.openSnackBar(error, 'error')
                                console.error(error)
                            }
                        });
                    }, error: error => {
                        this.snackbarService.openSnackBar(error, 'error')
                        console.error(error)
                    }
                });
                this.contentService.getMediaNotes(this.id).subscribe({
                    next: r => {
                        this.notes = r['Success'].reverse();
                        setTimeout((): void => {
                            // scroll to bottom of chat container
                            const element: HTMLElement | null = document.getElementById('chatContent');
                            if (element) {
                                element.scrollTop = element.scrollHeight;
                            }
                        }, 50);
                    }, error: error => {
                        this.snackbarService.openSnackBar(error, 'error')
                        console.error(error)
                    }
                });
            }, error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error);
            });

        this.hiddenForm = this.formBuilder.group({
            hidden_status: [],
        })
    }

    patchMedia(): void {
        this.loading = true;
        let body: {
            hidden_status?: string | null,
        } = {
            hidden_status: this.hiddenForm.value.hidden_status,
        }

        if (!this.id) return;
        this.contentService.patchMedia(this.id, body).subscribe({
            next: r => {
                this.loading = false;
                this.media = r;
                this.hiddenForm.patchValue({
                    hidden_status: this.media?.hidden_status,
                })
                this.snackbarService.openSnackBar('Media Updated', 'success')
            }, error: error => {
                this.loading = false;
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    moveMedia(): void {
        this.dialog.open(MoveMediaComponent, {
            data: {
                media: this.media,
                timelineId: this.timelineId,
                album: this.media?.memory,
                unhide: true
            },
            panelClass: 'fullWidthWindow',
        });
    }

    viewImage(): void {
        // stop all active media
        document.querySelectorAll('video').forEach(vid => vid.pause());
        document.querySelectorAll('audio').forEach(vid => vid.pause());
        const dialogRef: MatDialogRef<LightboxComponent> = this.dialog.open(LightboxComponent, {
            data: {
                media: [this.media],
                index: 0,
                permission: 'View, Comment and Add',
            },
            panelClass: 'fullWidthWindow',
        });
    }

    hide(): void {
        if (this.user?.role.can_manage_hidden_media) {
            const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
                panelClass: 'mediumWindow',
                data: {
                    showSubmitBtn: true,
                    showCancelBtn: true,
                    message: 'Are you sure you want to hide this media?',
                    title: 'Hide Media',
                    showHeader: true
                }
            });
            dialogRef.afterClosed().subscribe((resp): void => {
                if (resp && this.media?.id) {
                    this.contentService.hideMedia(this.media.id).subscribe({
                        next: r => {
                            this.snackbarService.openSnackBar('Media Hidden', 'success')
                            this.media = r;
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            console.error(error)
                        }
                    });
                }
            });
        } else {
            this.snackbarService.openSnackBar('This user cannot hide media', 'error')
        }
    }

    unhide(): void {
        if (this.user?.role.can_manage_hidden_media && this.media?.id) {
            this.contentService.unHideMedia(this.media.id).subscribe({
                next: r => {
                    this.snackbarService.openSnackBar('Media Approved to Memory Box', 'success')
                    this.media = r;
                }, error: error => {
                    this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
        } else {
            this.snackbarService.openSnackBar('This user cannot hide media', 'error')
        }
    }

    delete(): void {
        const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to delete this media? You cannot undo this action.',
                title: 'Delete Media',
                showHeader: true
            }
        });
        dialogRef.afterClosed().subscribe((resp): void => {
            if (resp && this.media?.id) {
                this.contentService.deleteMedia(this.media.id).subscribe({
                    next: r => {
                        this.snackbarService.openSnackBar('Media Deleted', 'success')

                    }, error: error => {
                        this.snackbarService.openSnackBar(error, 'error')
                        console.error(error)
                    }
                });
            }
        });
    }

    addNote(): void {
        if (!this.media?.id) return;
        this.contentService.postMediaNote(this.media.id, this.messageForm.value.message).subscribe({
            next: r => {
                this.messageForm.reset();
                this.notes = r['Success'].reverse();
                setTimeout((): void => {
                    // scroll to bottom of chat container
                    const element: HTMLElement | null = document.getElementById('chatContent');
                    if (element) {
                        element.scrollTop = element.scrollHeight;
                    }
                }, 50);
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    receivedToday(date: string): boolean {
        const messageDate: Date = new Date(date);
        return (this.today.toDateString() === messageDate.toDateString());
    }

    viewProfile(id: number): void {
        if (this.user?.role.can_access_profiles) {
            this.router.navigate(['/profile', id]);
        } else {
            this.snackbarService.openSnackBar('This user cannot access profiles', 'error')
        }
    }

    back(): void {
        if (history.length > 1) {
            history.back();
        } else {
            this.router.navigate(['/timeline']);
        }
    }
}
